/**
 * WHAT KIND OF THING, and HOW MANY OF THEM - two questions, not one.
 *
 * `bodyType` has four values and one of them is `horde`, which welded
 * the two together: a target could be a vehicle OR a mob, never a mob of
 * vehicles. The editor enforced it (`isHorde: newType === 'horde'`), the
 * factory enforced it, and the import validator actively reset the flag
 * to match - so even a hand-edited file could not express it (v8.44.0).
 *
 * The combat engine never believed it. `combatRules`, `healthRamp`,
 * `countdown` and `damageCalc` all read `horde.isHorde` on its own, and
 * `damageCalc` reads *either* signal. Only the three write paths
 * insisted, so this is the rule they should have been using.
 *
 * NOTHING STORED CHANGES FOR ANYTHING THAT ALREADY EXISTS. A humanoid
 * mob is still written as `bodyType: 'horde'` - the legacy value, with
 * its armour handling and its crit tables untouched. Only the new
 * combinations use the flag.
 */
import type { BodyTypeId } from '../types';

/** What a target can be SHAPED like. `horde` is not a shape. */
export type BodyShape = 'humanoid' | 'vehicle' | 'amorphous' | 'object';

export const BODY_SHAPES: BodyShape[] = ['humanoid', 'vehicle', 'amorphous', 'object'];

/** Enough of a Target to answer either question. */
export interface BodyTyped {
  bodyType?: string;
  horde?: { isHorde?: boolean };
}

/**
 * What kind of thing it is.
 *
 * `horde` resolves to `humanoid`: it says how many, and a mob with no
 * other type given is a mob of people.
 */
export function resolveBodyShape(target: BodyTyped | undefined | null): BodyShape {
  const declared = target?.bodyType;
  if (declared && (BODY_SHAPES as string[]).includes(declared)) return declared as BodyShape;
  return 'humanoid';
}

/**
 * An OBJECT (2026-10-26, plan cover-cue-and-objects): an inanimate thing to attack or defend - a generator, a
 * shrine. It takes no turn, never acts, never takes part in the fight's sides; it belongs to a team for its colours.
 * The one predicate every participant helper asks.
 */
export function isObject(target: BodyTyped | undefined | null): boolean {
  return target?.bodyType === 'object';
}

/**
 * The one condition an Object takes: its destruction (K). Every condition check asks this, so the editor, the attack
 * and a re-body cannot disagree about it.
 */
export const objectTakes = (code: string): boolean => code === 'K';

/** Is it a crowd rather than an individual? Either signal counts. */
export function isCrowd(target: BodyTyped | undefined | null): boolean {
  return target?.bodyType === 'horde' || !!target?.horde?.isHorde;
}

/**
 * The magnitude a crowd ATTACKS with (ruling 2026-10-09: +1 hit per 10): its magnitude left when it is a crowd, else
 * 0. Read off a projection (the live actor, the forecast's attacker), so it takes any record and never returns NaN.
 */
export function attackingMagnitude(actor: object | null | undefined): number {
  const crowd = actor as (BodyTyped & { horde?: { magnitude?: unknown } }) | null | undefined;
  if (!isCrowd(crowd)) return 0;
  const magnitude = Number(crowd?.horde?.magnitude);
  return Number.isFinite(magnitude) && magnitude > 0 ? magnitude : 0;
}

/** A horde's extra hits on each attack (ruling 2026-10-09): +1 per full 10 of magnitude. The ONE place the rule lives. */
export const crowdExtraHits = (magnitude: number): number => Math.floor(Math.max(0, magnitude) / 10);

/** Those extra hits in words - "+1 hit", "+3 hits" (`perAttack`: "... an attack") or "no extra hit", never a "+0". */
export function crowdHitsText(magnitude: number, perAttack = false): string {
  const n = crowdExtraHits(magnitude);
  if (n === 0) return 'no extra hit';
  return `+${n} hit${n === 1 ? '' : 's'}${perAttack ? ' an attack' : ''}`;
}

/**
 * The `bodyType` to STORE for a given shape and crowd-ness.
 *
 * A humanoid crowd keeps the legacy `horde` value rather than becoming
 * `humanoid` + a flag. Every saved session, every exported character and
 * every armour-and-crit code path already agrees on what that means, and
 * changing it would be a migration in exchange for tidiness.
 */
export function bodyTypeFor(shape: BodyShape, crowd: boolean): BodyTypeId {
  if (crowd && shape === 'humanoid') return 'horde';
  return shape;
}

/**
 * Does this combination have armour locations?
 *
 * A humanoid mob never has: that is how hordes have always worked here,
 * and every existing one is stored that way. A mob of vehicles keeps its
 * facings, because it is still made of vehicles - which is the whole
 * point of being able to say so.
 */
export function hasArmourLocations(shape: BodyShape, crowd: boolean): boolean {
  return !(crowd && shape === 'humanoid');
}
